import { useState, useEffect } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

// ─── POSettings ───────────────────────────────────────────────────────────────
// Distributor configures the company info that prints on every PO + default
// payment terms + supplier-specific account numbers.
//
// USAGE: drop into your Settings page as a section/card. Pass `getToken` and
// `showToast` from the parent (same pattern as BundleBuilder).
export default function POSettings({ getToken, showToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [info, setInfo]       = useState({
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    ap_email: '',
    terms_default: 'Net 30',
    account_numbers: {},  // { SUPPLIER_CODE: 'D12345' }
    carrier_accounts: {   // your own UPS/FedEx accounts for third-party freight billing
      ups:   { account_number: '', zip: '' },
      fedex: { account_number: '', zip: '' },
    },
  });
  const [counter, setCounter] = useState(1000);
  const [newAcctSupplier, setNewAcctSupplier] = useState('');
  const [newAcctNumber, setNewAcctNumber] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const data = await api.withToken(token).getPOSettings();
        setInfo({
          company: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          ap_email: '',
          terms_default: 'Net 30',
          account_numbers: {},
          ...(data.po_account_info || {}),
          carrier_accounts: {
            ups:   { account_number: '', zip: '' },
            fedex: { account_number: '', zip: '' },
            ...(data.po_account_info?.carrier_accounts || {}),
          },
        });
        setCounter(data.po_counter || 1000);
      } catch (e) {
        showToast?.('Failed to load PO settings', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken, showToast]);

  const update = (field, value) => setInfo(prev => ({ ...prev, [field]: value }));

  const updateCarrier = (carrier, field, value) => setInfo(prev => ({
    ...prev,
    carrier_accounts: {
      ...(prev.carrier_accounts || {}),
      [carrier]: { ...(prev.carrier_accounts?.[carrier] || {}), [field]: value },
    },
  }));

  const addAccountNumber = () => {
    const code = newAcctSupplier.trim().toUpperCase();
    const num = newAcctNumber.trim();
    if (!code || !num) return;
    setInfo(prev => ({
      ...prev,
      account_numbers: { ...(prev.account_numbers || {}), [code]: num },
    }));
    setNewAcctSupplier('');
    setNewAcctNumber('');
  };

  const removeAccountNumber = (code) => {
    setInfo(prev => {
      const next = { ...(prev.account_numbers || {}) };
      delete next[code];
      return { ...prev, account_numbers: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      await api.withToken(token).updatePOSettings({ po_account_info: info });
      showToast?.('PO settings saved', 'success');
    } catch (e) {
      showToast?.(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24, color: '#64748b' }}>Loading PO settings…</div>;

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
    borderRadius: 6, fontSize: 14, fontFamily: 'inherit',
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 };
  const groupStyle = { marginBottom: 14 };

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>Purchase Order Settings</div>
      <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: 13 }}>
        This information appears on every PO you generate. Required before you can create POs.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={groupStyle}>
          <label style={labelStyle}>Company name *</label>
          <input style={inputStyle} value={info.company} onChange={e => update('company', e.target.value)} />
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>Phone *</label>
          <input style={inputStyle} value={info.phone} onChange={e => update('phone', e.target.value)} />
        </div>
        <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Address line 1 *</label>
          <input style={inputStyle} value={info.address1} onChange={e => update('address1', e.target.value)} />
        </div>
        <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Address line 2</label>
          <input style={inputStyle} value={info.address2 || ''} onChange={e => update('address2', e.target.value)} placeholder="Suite, unit, etc. (optional)" />
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>City *</label>
          <input style={inputStyle} value={info.city} onChange={e => update('city', e.target.value)} />
        </div>
        <div style={{ ...groupStyle, display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8 }}>
          <div>
            <label style={labelStyle}>State *</label>
            <input style={inputStyle} maxLength={2} value={info.state} onChange={e => update('state', e.target.value.toUpperCase())} />
          </div>
          <div>
            <label style={labelStyle}>ZIP *</label>
            <input style={inputStyle} value={info.zip} onChange={e => update('zip', e.target.value)} />
          </div>
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>Accounts payable email *</label>
          <input style={inputStyle} type="email" value={info.ap_email} onChange={e => update('ap_email', e.target.value)} placeholder="ap@yourcompany.com" />
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>Default payment terms</label>
          <input style={inputStyle} value={info.terms_default || ''} onChange={e => update('terms_default', e.target.value)} placeholder="Net 30" />
        </div>
      </div>

      {/* Account numbers per supplier */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Supplier account numbers</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Your distributor account number with each supplier. Auto-fills on POs to that supplier.
        </div>

        {Object.entries(info.account_numbers || {}).length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {Object.entries(info.account_numbers || {}).map(([code, num]) => (
              <div key={code} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                background: '#f8fafc', borderRadius: 6, marginBottom: 6,
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, minWidth: 120 }}>{code}</div>
                <div style={{ fontSize: 13, color: '#334155', flex: 1 }}>{num}</div>
                <button
                  onClick={() => removeAccountNumber(code)}
                  style={{
                    border: 'none', background: 'transparent', color: '#dc2626',
                    cursor: 'pointer', fontSize: 13, padding: 4,
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            placeholder="Supplier code (e.g. HIT)"
            value={newAcctSupplier}
            onChange={e => setNewAcctSupplier(e.target.value)}
            style={{ ...inputStyle, flex: '1' }}
          />
          <input
            placeholder="Your account number"
            value={newAcctNumber}
            onChange={e => setNewAcctNumber(e.target.value)}
            style={{ ...inputStyle, flex: '1' }}
          />
          <button
            onClick={addAccountNumber}
            disabled={!newAcctSupplier.trim() || !newAcctNumber.trim()}
            style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid #cbd5e1',
              background: '#fff', cursor: 'pointer', fontSize: 13,
              opacity: (!newAcctSupplier.trim() || !newAcctNumber.trim()) ? 0.5 : 1,
            }}
          >
            Add
          </button>
        </div>
      </div>

      {/* Carrier shipping accounts (UPS / FedEx) for third-party freight billing */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Carrier shipping accounts</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Your own UPS / FedEx accounts. When set, you can tell a supplier to ship on your
          account instead of billing you for freight. Both fields are required per carrier —
          carriers need the account's billing zip to authorize third-party billing.
        </div>

        {[
          { key: 'ups', label: 'UPS' },
          { key: 'fedex', label: 'FedEx' },
        ].map(({ key, label }) => (
          <div key={key} style={{
            display: 'grid', gridTemplateColumns: '90px 1fr 140px', gap: 10,
            alignItems: 'center', marginBottom: 10,
          }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
            <input
              placeholder="Account number"
              value={info.carrier_accounts?.[key]?.account_number || ''}
              onChange={e => updateCarrier(key, 'account_number', e.target.value)}
              style={inputStyle}
            />
            <input
              placeholder="Billing zip"
              value={info.carrier_accounts?.[key]?.zip || ''}
              onChange={e => updateCarrier(key, 'zip', e.target.value)}
              style={inputStyle}
            />
          </div>
        ))}
      </div>

      <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          Next PO number: <strong>PO-{(counter || 1000) + 1}</strong>
        </div>
        <button
          onClick={save}
          disabled={saving}
          style={{
            padding: '10px 24px', background: G, color: '#fff', border: 'none',
            borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
